import { Fragment, ReactNode } from "react";
import cls from "./BlogContent.module.css";
import { ContentBlock } from "@/types/IBlogContent";
import { BlogCallout } from "./components/BlogCallout/BlogCallout";
import { BlogCtaBanner } from "./components/BlogCtaBanner/BlogCtaBanner";
import { BlogTable } from "./components/BlogTable/BlogTable";
import { BlogStages } from "./components/BlogStages/BlogStages";
import { BlogCaseCard } from "./components/BlogCaseCard/BlogCaseCard";
import { BlogChips } from "./components/BlogChips/BlogChips";
import { BlogQuote } from "./components/BlogQuote/BlogQuote";
import { BlogCode } from "./components/BlogCode/BlogCode";
import { AppCostCalculator } from "./components/AppCostCalculator/AppCostCalculator";

interface BlogContentProps {
    blocks: ContentBlock[];
    className?: string;
    /** Injected into the flow right before the first h2 — used for the mobile sidebar. */
    slot?: ReactNode;
}

const renderBlock = (block: ContentBlock, index: number) => {
    switch (block.type) {
        case "heading":
            return block.level === 2 ? (
                <h2 key={index} id={block.id} className={cls.h2}>{block.text}</h2>
            ) : (
                <h3 key={index} className={cls.h3}>{block.text}</h3>
            );

        case "paragraph":
            return (
                <p
                    key={index}
                    className={cls.paragraph}
                    dangerouslySetInnerHTML={{ __html: block.html }}
                />
            );

        case "list":
            return block.ordered ? (
                <ol key={index} className={cls.orderedList}>
                    {block.items.map((item, itemIndex) => (
                        <li key={itemIndex} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                </ol>
            ) : (
                <ul key={index} className={cls.list}>
                    {block.items.map((item, itemIndex) => (
                        <li key={itemIndex} dangerouslySetInnerHTML={{ __html: item }} />
                    ))}
                </ul>
            );

        case "callout":
            return <BlogCallout key={index} title={block.title} text={block.text} />;

        case "cta":
            return (
                <BlogCtaBanner
                    key={index}
                    title={block.title}
                    subtitle={block.subtitle}
                    buttonText={block.buttonText}
                    href={block.href}
                />
            );

        case "table":
            return <BlogTable key={index} columns={block.columns} rows={block.rows} />;

        case "stages":
            return <BlogStages key={index} items={block.items} />;

        case "cases":
            return (
                <div key={index} className={cls.cases}>
                    {block.items.map((item, itemIndex) => (
                        <BlogCaseCard
                            key={itemIndex}
                            title={item.title}
                            from={item.from}
                            to={item.to}
                            text={item.text}
                            tone={item.tone}
                            href={item.href}
                            linkText={item.linkText}
                        />
                    ))}
                </div>
            );

        case "chips":
            return <BlogChips key={index} items={block.items} />;

        case "stats":
            return (
                <dl key={index} className={cls.stats}>
                    {block.items.map(({ value, label }) => (
                        <div key={label} className={cls.stat}>
                            <dt>{label}</dt>
                            <dd>{value}</dd>
                        </div>
                    ))}
                </dl>
            );

        case "quote":
            return <BlogQuote key={index} html={block.html} />;

        case "code":
            return <BlogCode key={index} code={block.code} />;

        case "appCostCalculator":
            return <AppCostCalculator key={index} />;
    }
};

export const BlogContent = ({ blocks, className = "", slot }: BlogContentProps) => {
    const slotIndex = blocks.findIndex((block) => block.type === "heading" && block.level === 2);

    return (
        <div className={`${cls.content} ${className}`}>
            {blocks.map((block, index) => (
                <Fragment key={index}>
                    {slot && index === slotIndex && slot}
                    {renderBlock(block, index)}
                </Fragment>
            ))}
        </div>
    );
};
